import React, { useState, useRef } from "react";
import { Clock, Brain, Plus, Send, StopCircle, X, Bot } from "lucide-react";
import { Button, Textarea } from "@/components/ui";
import { useHistoryStore } from "@/store/HistoryStore";
import { useDeepThinkStore } from "@/store/DeepThinkStore";
import { useChatContext } from "@/components/chatview/ChatContext";
import { AttachmentPreview } from "./AttachmentPreview";
import { QuotePreview } from "./QuotePreview";
import { UploadFileDrawer } from "./UploadFileDrawer";
import { cn } from "@/lib/utils";
import type { Attachment } from "@/types/chat";
import { useChatIdStore } from "@/store/ChatIdStore";
import { useAgentStore } from "@/store/AgentStore";

interface InputAreaProps {
  input: string;
  setInput: (value: string) => void;
  isLoading: boolean;
  onSubmit: () => void;
  onStop: () => void;
  onFileClick: () => void;
  onFileChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    options?: { assumeImage?: boolean },
  ) => void;
  disabled: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  handleInput: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  attachments?: Attachment[];
  onRemoveAttachment?: (id: string) => void;
  onFilePreview?: (
    attachment: any,
    fileUrl: string | null,
    fileName: string
  ) => void;
  connectedTop?: boolean;
}

export function InputArea({
  input,
  isLoading,
  onSubmit,
  onStop,
  onFileClick,
  onFileChange,
  disabled,
  textareaRef,
  handleInput,
  attachments = [],
  onRemoveAttachment,
  onFilePreview,
  connectedTop = false,
}: InputAreaProps) {
  // @ts-ignore 必要的state参数，忽略类型检查
  const [activeTab, setActiveTab] = useState("history");
  const { useHistory, setUseHistory } = useHistoryStore();
  const { deepThink, setDeepThink } = useDeepThinkStore();
  const { activeQuote, setActiveQuote } = useChatContext();
  const { setStartChatId } = useChatIdStore();
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const currentAgent = useAgentStore((state) => state.currentAgent);
  const agentDismissed = useAgentStore((state) => state.agentDismissed);
  const setAgentDismissed = useAgentStore((state) => state.setAgentDismissed);
  const getDefaultAgent = useAgentStore((state) => state.getDefaultAgent);

  const defaultAgent = getDefaultAgent();
  const isDefaultAgent = currentAgent?.id === defaultAgent?.id;
  const showAgentIndicator = !isDefaultAgent && !agentDismissed;
  const placeholderText = (isDefaultAgent || agentDismissed) 
    ? "向匹配与决策智能体引擎系统发消息" 
    : `向${currentAgent?.name}发送消息`;

  // 拍照的变量
  // const cameraInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // 拍照
  // const handleCameraClick = () => {
  //   cameraInputRef.current?.click();
  // };

  // 图片选择
  const handleImageClick = () => {
    imageInputRef.current?.click();
  };

  const handleFileClick = () => {
    onFileClick();
  };

  return (
    <div className="w-full">
      <div
        className={cn(
          "w-full bg-white p-4 mx-auto",
          "transition-all duration-200 ease-in-out",
          connectedTop
            ? "rounded-b-[21px] pt-2"
            : "shadow-[0_-2px_10px_5px_#F7F7F7] rounded-[21px_21px_0_0]"
        )}
      >
        {/* 引用预览面板 - 移入内部 */}
        {activeQuote && (
          <div className="mb-3">
            <QuotePreview
              quote={activeQuote}
              onCancel={() => {
                setActiveQuote?.(null);
                setStartChatId(0);
              }}
              className="rounded-xl border-none bg-gray-100"
            />
          </div>
        )}

        {/* 附件预览 - 移入内部 */}
        {attachments.length > 0 && (
          <div className="w-full mb-3">
            <AttachmentPreview
              attachments={attachments}
              onRemove={onRemoveAttachment}
              onPreview={onFilePreview}
            />
          </div>
        )}
        {/* 拍照 ，考虑移除*/}
        {/* <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFileChange}
      /> */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(event) => onFileChange?.(event, { assumeImage: true })}
        />
        {/* 当前智能体提示 */}
        {showAgentIndicator && currentAgent && (
          <div className="w-full mb-3 flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <div 
              className="w-6 h-6 rounded flex items-center justify-center"
              style={{ backgroundColor: currentAgent.iconBgColor }}
            >
              <Bot className="w-4 h-4" style={{ color: currentAgent.iconColor }} />
            </div>
            <span className="flex-1 text-sm text-blue-700">当前智能体：{currentAgent.name}</span>
            <button
              onClick={() => {
                setAgentDismissed(true);
              }}
              className="p-1 hover:bg-blue-100 rounded"
            >
              <X className="w-4 h-4 text-blue-500" />
            </button>
          </div>
        )}

        <div className="w-full mb-3">
          <Textarea
            ref={textareaRef}
            id="chat-input"
            name="chat-input"
            placeholder={placeholderText}
            value={input}
            onChange={handleInput}
            className="w-full overflow-y-auto resize-none text-base border-none p-0 focus-visible:ring-0 shadow-none bg-transparent placeholder:text-base font-normal"
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (isLoading) {
                  console.error(
                    "Please wait for the model to finish its response!"
                  );
                } else {
                  onSubmit();
                }
              }
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setActiveTab("history");
                setUseHistory(useHistory === 0 ? 1 : 0);
              }}
              className={`gap-1 rounded-full px-4 py-2 transition-all duration-200 ${
                useHistory === 1
                  ? "bg-blue-100 text-blue-600 hover:bg-blue-100 hover:text-blue-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Clock className="h-4 w-4" />
              <span className="text-sm font-medium">
                {useHistory === 1 ? "使用历史" : "不用历史"}
              </span>
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                setActiveTab("plugin");
                setDeepThink(deepThink === 0 ? 1 : 0);
              }}
              className={`gap-1 rounded-full px-4 py-2 h-auto transition-all duration-200 ${
                deepThink === 1
                  ? "bg-blue-100 text-blue-600 hover:bg-blue-100 hover:text-blue-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Brain className="h-4 w-4" />
              <span className="text-sm font-medium">
                {deepThink === 1 ? "深度思考" : "普通模式"}
              </span>
            </Button>
          </div>

          {/* 右侧操作按钮 */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={(event) => {
                event.preventDefault();
                (event.currentTarget as HTMLButtonElement).blur();
                setUploadDrawerOpen(true);
              }}
              className="h-8 w-8 rounded-full text-slate-700 hover:bg-slate-100"
              disabled={isLoading}
            >
              <Plus className="h-5 w-5" />
            </Button>

            {isLoading ? (
              <Button
                size="icon"
                onClick={(event) => {
                  event.preventDefault();
                  onStop();
                }}
                className="h-8 w-8 rounded-full bg-red-500 text-white hover:bg-red-600 flex items-center justify-center shadow-sm hover:shadow"
              >
                <StopCircle className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                size="icon"
                onClick={(event) => {
                  event.preventDefault();
                  if (!disabled) {
                    onSubmit();
                  }
                }}
                className="h-8 w-8 rounded-full bg-blue-500 text-white hover:bg-blue-600 flex items-center justify-center shadow-sm hover:shadow"
                disabled={disabled}
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* 上传文件抽屉 */}
        <UploadFileDrawer
          open={uploadDrawerOpen}
          onOpenChange={setUploadDrawerOpen}
          // onCameraClick={handleCameraClick}
          onImageClick={handleImageClick}
          onFileClick={handleFileClick}
        />
      </div>
    </div>
  );
}
